import React, { useState, useEffect, lazy, Suspense } from "react";
import {
  BrowserRouter as Router,
  Routes,
  Route,
  Navigate,
  useNavigate,
  useLocation,
} from "react-router-dom";
import ThemeProviderWrapper from "./ThemeProviderWrapper";
import { themes } from "./themes";
import { ResponsiveProvider } from "./contexts/ResponsiveContext";
import Header from "./components/Header";
import AuthForm from "./components/AuthForm";
import ConfirmSignUp from "./components/ConfirmSignUp";
import ProtectedRoute from "./components/ProtectedRoute";
import Loading from "./components/Loading";
import AuthGuard from "./components/AuthGuard";
import ThemeBootstrapper from "./components/ThemeBootstrapper";
import CssBaseline from "@mui/material/CssBaseline";
import {
  clearAuthState,
  isAuthenticated as checkAuth,
  isTokenExpired,
} from "./utils/authUtils";
import {
  preventAWSRedirects,
  clearAllAWSState,
} from "./utils/awsRedirectPrevention";

const Dashboard = lazy(() => import("./components/Dashboard"));
const DocsLayout = lazy(() => import("./components/docs/DocsLayout"));

// Blocks Safari swipe-back gesture on protected routes
// eslint-disable-next-line no-unused-vars -- intentionally not rendered (disabled); see note at former usage site
const SwipeNavigationBlocker = ({ isAuthenticated }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const currentPathRef = React.useRef(location.pathname);
  const isBlockingRef = React.useRef(false);

  useEffect(() => {
    currentPathRef.current = location.pathname;
    isBlockingRef.current = false;
  }, [location.pathname]);

  useEffect(() => {
    if (!isAuthenticated) return;
    const publicPages = ["/login", "/register", "/confirm-signup"];
    if (publicPages.some((p) => location.pathname === p)) return;

    const handlePopState = () => {
      if (isBlockingRef.current) return;
      const targetPath = currentPathRef.current;
      if (window.location.pathname !== targetPath) {
        isBlockingRef.current = true;
        window.history.replaceState(null, "", targetPath);
        navigate(targetPath, { replace: true });
        setTimeout(() => { isBlockingRef.current = false; }, 100);
      }
    };

    window.history.pushState({ preventBack: true }, "", currentPathRef.current);
    window.addEventListener("popstate", handlePopState, { passive: true });
    return () => window.removeEventListener("popstate", handlePopState);
  }, [isAuthenticated, location.pathname, navigate]);

  return null;
};

const App = () => {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [theme, setTheme] = useState(() => {
    try {
      // Only honor a saved personal theme for AUTHENTICATED users; unauth visitors
      // get the brand default (refined by the no-store /site-theme.json fetch) so a
      // stale localStorage theme can never flash on first paint.
      const token = localStorage.getItem("jwtToken");
      if (token && !isTokenExpired(token)) {
        const saved = localStorage.getItem("THEME");
        if (saved) return JSON.parse(saved);
      }
    } catch {}
    return themes.midnight;
  });

  useEffect(() => {
    fetch("/site-theme.json", { cache: "no-store" })
      .then((r) => (r.ok ? r.json() : null))
      .then((t) => { if (t?.primaryColor) handleThemeChange(t); })
      .catch(() => {});
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    const cleanup = preventAWSRedirects();
    const existingToken = localStorage.getItem("jwtToken");
    if (existingToken && !isTokenExpired(existingToken)) {
      setIsAuthenticated(true);
    } else {
      clearAllAWSState();
      clearAuthState();
      setIsAuthenticated(false);
    }
    return cleanup;
  }, []);

  // Token expiration + inactivity check
  useEffect(() => {
    if (!isAuthenticated) return;

    const checkInterval = setInterval(() => {
      const token = localStorage.getItem("jwtToken");
      if (token && isTokenExpired(token)) {
        clearAuthState();
        setIsAuthenticated(false);
        window.location.replace("/login");
        return;
      }
      const lastActivity = localStorage.getItem("lastActivityTime");
      if (lastActivity && Date.now() - parseInt(lastActivity, 10) > 30 * 60 * 1000) {
        clearAuthState();
        setIsAuthenticated(false);
        window.location.replace("/login");
      }
    }, 30000);

    const updateActivity = () => {
      if (isAuthenticated) localStorage.setItem("lastActivityTime", Date.now().toString());
    };
    const events = ["mousedown", "mousemove", "keypress", "scroll", "touchstart", "click"];
    events.forEach((e) => window.addEventListener(e, updateActivity, { passive: true }));

    return () => {
      clearInterval(checkInterval);
      events.forEach((e) => window.removeEventListener(e, updateActivity));
    };
  }, [isAuthenticated]);

  const handleThemeChange = (newTheme) => {
    setTheme(newTheme);
    localStorage.setItem("THEME", JSON.stringify(newTheme));
  };

  return (
    <Router>
      <ResponsiveProvider>
        <ThemeProviderWrapper theme={theme}>
          <CssBaseline />
          <ThemeBootstrapper
            isAuthenticated={isAuthenticated}
            onThemeLoaded={handleThemeChange}
          />
          {/* SwipeNavigationBlocker disabled: it blocked legitimate browser back-navigation and caused blank-page-on-back via bfcache. */}
          <AuthGuard>
            <Header
              isAuthenticated={isAuthenticated}
              setIsAuthenticated={setIsAuthenticated}
            />
            <Suspense fallback={<Loading />}>
              <Routes>
                {/* Public routes */}
                <Route path="/" element={<Dashboard isAuthenticated={isAuthenticated} />} />
                <Route
                  path="/login"
                  element={<AuthForm setIsAuthenticated={setIsAuthenticated} />}
                />
                <Route
                  path="/register"
                  element={<AuthForm setIsAuthenticated={setIsAuthenticated} />}
                />
                <Route path="/confirm-signup" element={<ConfirmSignUp />} />

                {/* Docs — public paths visible to all; private paths gate at render */}
                <Route
                  path="/docs/*"
                  element={<DocsLayout isAuthenticated={isAuthenticated} />}
                />

                {/* Catch-all: redirect unauthenticated users away from unknown routes */}
                {!isAuthenticated && (
                  <Route path="*" element={<Navigate to="/login" replace />} />
                )}
              </Routes>
            </Suspense>
          </AuthGuard>
        </ThemeProviderWrapper>
      </ResponsiveProvider>
    </Router>
  );
};

export default App;
